import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
  setMultiplePayments,
  setIsLoadingPayments,
  clearMultiplePayments,
  setPaymentMethods,
} from '../../store/slices/sale/multiplePaymentSlice';

import MultiplePaymentService from '../../services/sale/MultiplePaymentService';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useModals from '../useModals';
import { useCallback } from 'react';

export const useMultiplePayment = () => {
  const dispatch: AppDispatch = useDispatch();
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const { openModal } = useModals();

  const {
    payments,
    totalToPay,
    totalEntered,
    pendingAmount,
    isLoading,
    paymentMethods
  } = useSelector((state: RootState) => state.multiplePayment);

  // ===============================
  // 📊 FETCH PAYMENTS (STATE GLOBAL)
  // ===============================
  const fetchPayments = useCallback(async (saleId: number) => {
    try {
      dispatch(setIsLoadingPayments(true));

      const response = await MultiplePaymentService.getPaymentsWithSummary(saleId);
      // console.log(response);
      const { statusCode, data } = response;

      if (statusCode === 200 && data) {
        dispatch(setMultiplePayments(data));
      }
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setIsLoadingPayments(false));
    }
  }, [dispatch]);

  // ===============================
  // ➕ ADD PAYMENT
  // ===============================
  const createPayment = async (saleId: number, dto: any) => {
    try {
      const response = await MultiplePaymentService.addPayment(saleId, dto);
      const { statusCode, data, type, title, message } = response;

      if (statusCode === 201 && data) {
        dispatch(setMultiplePayments(data));
        openModal(type, { title, message });
        return true;
      }

      show('error', 'Error', 'No se pudo registrar el pago.');
      return false;

    } catch (error) {
      handle(error);
      return false;
    }
  };

  // ===============================
  // ✏️ UPDATE PAYMENT
  // ===============================
  const updatePaymentById = async (saleId: number, paymentId: number, dto: any) => {
    try {
      const response = await MultiplePaymentService.updatePayment(saleId, paymentId, dto);
      const { statusCode, data } = response;

      if (statusCode === 200 && data) {
        dispatch(setMultiplePayments(data));
        return true;
      }

      show('error', 'Error', 'No se pudo actualizar el pago.');
      return false;

    } catch (error) {
      handle(error);
      return false;
    }
  };

  // ===============================
  // 🗑️ DELETE PAYMENT
  // ===============================
  const deletePayment = async (saleId: number, paymentId: number) => {
    try {
      const response = await MultiplePaymentService.deletePayment(saleId, paymentId);
      const { statusCode, data, type, title, message } = response;

      if (statusCode === 200 && data) {
        dispatch(setMultiplePayments(data));
        openModal(type, { title, message });
        return true;
      }

      show('error', 'Error', 'No se pudo eliminar el pago.');
      return false;

    } catch (error) {
      handle(error);
      return false;
    }
  };

  // ===============================
  // 🗑️ DELETE ALL PAYMENTS (backend)
  // ===============================
  const deleteAllPayments = async (saleId: number) => {
    try {
      const response = await MultiplePaymentService.deleteAllPayments(saleId);
      const { statusCode, data, type, title, message } = response;

      if (statusCode === 200 && data) {
        dispatch(setMultiplePayments(data));
        openModal(type, { title, message });
        return true;
      }

      show('error', 'Error', 'No se pudieron eliminar los pagos.');
      return false;

    } catch (error) {
      handle(error);
      return false;
    }
  };

  // ===============================
  // 💳 PAYMENT METHODS
  // ===============================
  const getAvailableMethods = async () => {
    try {
      const response = await MultiplePaymentService.getAvailableMethods();
      const { statusCode, data } = response;

      if (statusCode === 200 && data) {
        dispatch(setPaymentMethods(data));
      }

    } catch (error) {
      handle(error);
    }
  };

  // ===============================
  // 🧹 CLEAR
  // ===============================
  const clearAllPayments = () => {
    dispatch(clearMultiplePayments());
  };

  return {
    // state
    payments,
    totalToPay,
    totalEntered,
    pendingAmount,
    isLoading,
    paymentMethods,

    // actions
    fetchPayments,
    createPayment,
    updatePaymentById,
    deletePayment,
    deleteAllPayments,
    getAvailableMethods,
    clearAllPayments,
  };
};